import {
  AlertTriangleIcon,
  CalendarIcon,
  CreditCardIcon,
  MegaphoneIcon,
  UsersIcon,
  ZapIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Brain } from '@/shared/types/brain';
import { Campaign, CampaignSender } from '@/shared/types/campaigns';
import { timeFromNow } from '@/util/dateUtils';

import { useExportCampaignLogs } from '../../hooks/useExportCampaignLogs';

interface Props {
  campaign?: Campaign;
  brain?: Brain;
  senders: CampaignSender[];
  isLoading?: boolean;
}

const TYPE_LABEL_KEYS: Record<string, string> = {
  marketing: 'campaigns.outreach',
  sales: 'campaigns.outreach',
  utility: 'campaigns.outreach',
  payment_recovery: 'campaigns.paymentRecovery',
};

const formatPct = (num: number, den: number): string =>
  den ? ((num / den) * 100).toFixed(1) : '0.0';

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Redesigned overview card: campaign metadata (type, completion, audience,
 * response handling, errors) plus a conversion-rate table. Sits directly on the
 * grey analytics panel — no card chrome of its own.
 */
export const CampaignOverviewCard = ({ campaign, brain, senders, isLoading }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  const { exportLogs, isExporting } = useExportCampaignLogs({
    campaignId: campaign?.campaign_id,
    campaignName: campaign?.name,
    params: {},
    campaignSenders: senders,
  });

  const sent = campaign?.sent_count || 0;
  const delivered = campaign?.delivered_count || 0;
  const opened = campaign?.read_count || 0;
  const replied = campaign?.replied_count || 0;

  const TypeIcon = campaign?.type === 'payment_recovery' ? CreditCardIcon : MegaphoneIcon;
  const completionTimestamp = campaign?.schedule?.end_at ?? campaign?.updated;

  const getTypeLabel = (): string => {
    const key = campaign?.type ? TYPE_LABEL_KEYS[campaign.type] : undefined;
    if (key) {
      return t(key);
    }
    return campaign?.type ? capitalize(campaign.type) : '—';
  };

  // Brain version 0 is the unpublished draft — show "Draft" rather than "v0".
  const getBrainVersionSuffix = (): string => {
    if (campaign?.brain_version == null) {
      return '';
    }
    return campaign.brain_version === 0
      ? ` - ${t('broadcasts.status.draft')}`
      : ` - v${campaign.brain_version}`;
  };

  const handleGoToAiAgent = () => {
    if (slug && campaign?.brain_parent_id) {
      navigate(`/${slug}/ai-agents/${campaign.brain_parent_id}`);
    }
  };

  return (
    <div className="flex-1 p-6">
      <h2 className="mb-4 text-lg leading-none font-semibold text-foreground">
        {t('campaigns.analytics.overview')}
      </h2>

      <div className="space-y-2">
        {/* Type */}
        <div className="grid grid-cols-[12rem_1fr] items-center">
          <span className="text-sm text-foreground-muted">{t('campaigns.analytics.type')}</span>
          {isLoading ? (
            <Skeleton className="h-5 w-28" />
          ) : (
            <div className="flex items-center gap-2">
              <TypeIcon size={16} className="text-foreground-muted" />
              <span className="text-sm leading-6 font-medium text-foreground">
                {getTypeLabel()}
              </span>
            </div>
          )}
        </div>

        {/* Completion */}
        <div className="grid grid-cols-[12rem_1fr] items-center">
          <span className="text-sm text-foreground-muted">
            {t('campaigns.analytics.completion')}
          </span>
          {isLoading ? (
            <Skeleton className="h-5 w-24" />
          ) : (
            <div className="flex items-center gap-2">
              <CalendarIcon size={16} className="text-foreground-muted" />
              <span className="text-sm leading-6 font-medium text-foreground">
                <TimestampTooltip timestamp={completionTimestamp} side="left">
                  {timeFromNow(completionTimestamp)}
                </TimestampTooltip>
              </span>
            </div>
          )}
        </div>

        {/* Audience — clickable to download */}
        <div className="grid grid-cols-[12rem_1fr] items-center">
          <span className="text-sm text-foreground-muted">{t('campaigns.audience')}</span>
          {isLoading ? (
            <Skeleton className="h-6 w-16 rounded-full" />
          ) : (
            <div>
              <InfoTooltip content={t('campaigns.analytics.download_audience')}>
                <button
                  type="button"
                  onClick={() => exportLogs('csv')}
                  disabled={isExporting || !campaign?.subscribers_count}
                  className="disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Badge
                    type="secondary"
                    flavour="neutral"
                    rounded
                    leadingIcon={<UsersIcon />}
                    className="-ml-2 cursor-pointer border-stroke bg-background text-foreground"
                  >
                    {campaign?.subscribers_count || 0}
                  </Badge>
                </button>
              </InfoTooltip>
            </div>
          )}
        </div>

        {/* Response handling — clickable to AI agent */}
        <div className="grid grid-cols-[12rem_1fr] items-center">
          <span className="text-sm text-foreground-muted">
            {t('campaigns.analytics.response_handling')}
          </span>
          {isLoading ? (
            <Skeleton className="h-6 w-40 rounded-full" />
          ) : (
            <div className="min-w-0">
              {brain?.name ? (
                <InfoTooltip content={t('campaigns.analytics.go_to_ai_agent')}>
                  <button
                    type="button"
                    onClick={handleGoToAiAgent}
                    disabled={!campaign?.brain_parent_id}
                    className="max-w-full min-w-0 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Badge
                      type="secondary"
                      flavour="neutral"
                      rounded
                      leadingIcon={<ZapIcon />}
                      className="-ml-2 cursor-pointer border-stroke bg-background text-foreground"
                    >
                      {brain.name}
                      {getBrainVersionSuffix()}
                    </Badge>
                  </button>
                </InfoTooltip>
              ) : (
                <span className="text-sm leading-6 font-medium text-foreground">
                  {t('campaigns.not_assigned')}
                </span>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* Errors */}
        <div className="grid grid-cols-[12rem_1fr] items-center">
          <span className="text-sm text-foreground-muted">{t('campaigns.analytics.errors')}</span>
          {isLoading ? (
            <Skeleton className="h-5 w-8" />
          ) : (
            <div className="flex items-center gap-2">
              <AlertTriangleIcon size={16} className="text-foreground-muted" />
              <span className="text-sm leading-6 font-medium text-foreground">
                {campaign?.failed_count || 0}
              </span>
            </div>
          )}
        </div>

        <Separator />

        {/* Metrics table */}
        <div className="space-y-2 pt-1">
          {/* Sent */}
          <div className="grid grid-cols-[12rem_auto_1fr] items-center">
            <span className="text-sm text-foreground-muted">{t('campaigns.analytics.sent')}</span>
            {isLoading ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <span className="text-sm leading-6 font-semibold text-foreground">{sent}</span>
            )}
            <span className="text-right text-sm text-foreground-muted">
              {t('campaigns.analytics.cvr_header')}
            </span>
          </div>

          {/* Delivered */}
          <div className="grid grid-cols-[12rem_auto_1fr] items-center">
            <span className="text-sm text-foreground-muted">
              {t('campaigns.analytics.delivered')}
            </span>
            {isLoading ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <span className="text-sm leading-6 font-semibold text-foreground">{delivered}</span>
            )}
            {isLoading ? (
              <Skeleton className="ml-auto h-5 w-28" />
            ) : (
              <InfoTooltip
                content={
                  <>
                    <p className="font-semibold">CVR: Delivered / Sent</p>
                    <p>Total CVR: Delivered / Sent</p>
                  </>
                }
                size="large"
              >
                <span className="block cursor-default text-right text-sm text-foreground-muted">
                  {formatPct(delivered, sent)}% | ({formatPct(delivered, sent)}%)
                </span>
              </InfoTooltip>
            )}
          </div>

          {/* Opened */}
          <div className="grid grid-cols-[12rem_auto_1fr] items-center">
            <span className="text-sm text-foreground-muted">{t('campaigns.analytics.opened')}</span>
            {isLoading ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <span className="text-sm leading-6 font-semibold text-foreground">{opened}</span>
            )}
            {isLoading ? (
              <Skeleton className="ml-auto h-5 w-28" />
            ) : (
              <InfoTooltip
                content={
                  <>
                    <p className="font-semibold">CVR: Opened / Delivered</p>
                    <p>Total CVR: Opened / Sent</p>
                  </>
                }
                size="large"
              >
                <span className="block cursor-default text-right text-sm text-foreground-muted">
                  {formatPct(opened, delivered)}% | ({formatPct(opened, sent)}%)
                </span>
              </InfoTooltip>
            )}
          </div>

          {/* Replied */}
          <div className="grid grid-cols-[12rem_auto_1fr] items-center">
            <span className="text-sm text-foreground-muted">
              {t('campaigns.analytics.replied')}
            </span>
            {isLoading ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <span className="text-sm leading-6 font-semibold text-foreground">{replied}</span>
            )}
            {isLoading ? (
              <Skeleton className="ml-auto h-5 w-28" />
            ) : (
              <InfoTooltip
                content={
                  <>
                    <p className="font-semibold">CVR: Replied / Opened</p>
                    <p>Total CVR: Replied / Sent</p>
                  </>
                }
                size="large"
              >
                <span className="block cursor-default text-right text-sm text-foreground-muted">
                  {formatPct(replied, opened)}% | ({formatPct(replied, sent)}%)
                </span>
              </InfoTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
